import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import {
  hostedEvents,
  money,
  type HostedEventFilters,
  type HostedEventSummary,
} from '../../services/hostedEvents'
import { dateRange, errorMessage } from './helpers'
import {
  Empty,
  ErrorBanner,
  Pager,
  Spinner,
  StatusBadge,
  clickableRow,
  inputClass,
  tableWrap,
  theadClass,
} from './ui'

const LIMIT = 25

export function HostedEvents() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<HostedEventFilters>({
    status: '',
    timeframe: '',
    rebates: '',
    search: '',
    offset: 0,
  })
  const [searchInput, setSearchInput] = useState('')
  const search = useDebouncedValue(searchInput, 300)
  const [items, setItems] = useState<HostedEventSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setFilters((current) =>
      current.search === search ? current : { ...current, search, offset: 0 }
    )
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await hostedEvents.list({ ...filters, limit: LIMIT })
      if (result.items.length === 0 && (filters.offset || 0) > 0) {
        const last = Math.max(0, (Math.ceil(result.total / LIMIT) - 1) * LIMIT)
        setFilters((current) => ({ ...current, offset: last }))
        return
      }
      setItems(result.items)
      setTotal(result.total)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    load()
  }, [load])

  const change = (key: keyof HostedEventFilters, value: string) =>
    setFilters((current) => ({ ...current, [key]: value, offset: 0 }))

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">
          Hosted Events
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Markets run on Barta. Open one to see its vendors and manage a sponsored-table rebate.
        </p>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <input
          className={inputClass}
          placeholder="Search name, city, or slug"
          aria-label="Search events"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <select
          className={inputClass}
          aria-label="When"
          value={filters.timeframe}
          onChange={(event) => change('timeframe', event.target.value)}
        >
          <option value="">Any time</option>
          <option value="upcoming">Upcoming and live</option>
          <option value="past">Past</option>
        </select>
        <select
          className={inputClass}
          aria-label="Event status"
          value={filters.status}
          onChange={(event) => change('status', event.target.value)}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="applications_open">Applications open</option>
          <option value="applications_closed">Applications closed</option>
          <option value="day_of">Day of</option>
          <option value="wrapped">Wrapped</option>
        </select>
        <select
          className={inputClass}
          aria-label="Rebate program"
          value={filters.rebates}
          onChange={(event) => change('rebates', event.target.value)}
        >
          <option value="">With or without a rebate</option>
          <option value="with">Has a rebate program</option>
          <option value="without">No rebate program</option>
        </select>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Empty>No hosted events match these filters.</Empty>
      ) : (
        <div className={tableWrap}>
          <table className="w-full text-sm">
            <thead className={theadClass}>
              <tr>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Organizer</th>
                <th className="px-4 py-3">Dates</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Paid vendors</th>
                <th className="px-4 py-3 text-right">Checked in</th>
                <th className="px-4 py-3">Rebate</th>
                <th className="px-4 py-3 text-right">Committed</th>
              </tr>
            </thead>
            <tbody>
              {items.map((event) => (
                <tr
                  key={event.id}
                  className={clickableRow}
                  tabIndex={0}
                  onClick={() => navigate(`/hosted-events/${event.id}`)}
                  onKeyDown={(key) => key.key === 'Enter' && navigate(`/hosted-events/${event.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">{event.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {[event.city, event.state_code].filter(Boolean).join(', ') || 'No city'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {event.organizer_name || 'Unknown'}
                    {event.organizer_handle && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        @{event.organizer_handle}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {dateRange(event.starts_at, event.ends_at)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={event.status} />
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                    {event.paid_vendors}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                    {event.checked_in_vendors}
                  </td>
                  <td className="px-4 py-3">
                    {event.rebate_program_status ? (
                      <div className="flex items-center gap-2">
                        <StatusBadge status={event.rebate_program_status} />
                        {event.rebate_pending_count > 0 && (
                          <span className="text-xs text-amber-700 dark:text-amber-300">
                            {event.rebate_pending_count} to review
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                    {event.rebate_program_status ? money(event.rebate_committed_cents) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager
        offset={filters.offset || 0}
        limit={LIMIT}
        total={total}
        onChange={(offset) => setFilters((current) => ({ ...current, offset }))}
      />
    </div>
  )
}
